import type { CalendarEventFeedbackQuestion } from 'shared-types';

type Params = {
  answer: unknown;
  question: CalendarEventFeedbackQuestion;
};

const isNonEmptyString = (value: unknown): value is string =>
  typeof value === 'string' && Boolean(value.trim());

/**
 * Re-validates a single submitted answer against the question it claims to
 * answer. The client never dictates whether an answer is acceptable - every
 * rule enforced on the question itself (required options, selection counts,
 * star bounds) is checked again here before anything is persisted.
 */
const isCalendarEventFeedbackAnswerValid = ({
  answer,
  question
}: Params): boolean => {
  if (!answer || typeof answer !== 'object') return false;

  const value = answer as { type?: unknown };
  if (value.type !== question.type) return false;

  if (question.type === 'openquestion') {
    const { text } = answer as { text?: unknown };
    if (typeof text !== 'string') return false;

    return isNonEmptyString(text) || !question.isRequired;
  }

  if (question.type === 'starrating') {
    const { rating } = answer as { rating?: unknown };

    return (
      typeof rating === 'number' &&
      Number.isInteger(rating) &&
      rating >= 1 &&
      rating <= question.data.numberOfStars
    );
  }

  // singlechoice or multiplechoice
  const { optionIds } = answer as { optionIds?: unknown };
  if (!Array.isArray(optionIds) || !optionIds.every(isNonEmptyString)) {
    return false;
  }

  const validOptionIds = new Set(question.data.options.map(({ id }) => id));
  const uniqueOptionIds = new Set(optionIds);
  if (
    uniqueOptionIds.size !== optionIds.length ||
    !optionIds.every((id) => validOptionIds.has(id))
  ) {
    return false;
  }

  return (
    optionIds.length >= question.data.numberOfAllowedSelectionsMin &&
    optionIds.length <= question.data.numberOfAllowedSelectionsMax
  );
};

export default isCalendarEventFeedbackAnswerValid;
